import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { Formik, useField } from 'formik';
import {
  CompanyPageCompanyFragment,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  Button,
  EditFormWrapper,
  FormikRadioButtonGroup,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './InterviewEvaluationSection.module.scss';

export const dataTestId = 'interview-evaluation-form';

type InterviewEvaluationSectionProps = {
  company: CompanyPageCompanyFragment;
};

export default function InterviewEvaluationSection({
  company,
}: InterviewEvaluationSectionProps) {
  const [isEditing, setIsEditing] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  return isEditing ? (
    <InterviewEvaluationForm
      company={company}
      onCancelClick={handleCancelClick}
      onSave={handleSave}
    />
  ) : (
    <InterviewEvaluationSectionView
      company={company}
      onEditClick={handleEditClick}
    />
  );
}

type InterviewEvaluationSectionViewProps = {
  company: CompanyPageCompanyFragment;
  onEditClick: () => void;
};

function InterviewEvaluationSectionView({
  company,
  onEditClick,
}: InterviewEvaluationSectionViewProps) {
  return (
    <div className={styles.viewContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Interview Evaluation</h3>
        <Button variant="secondary" size="medium" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        <InfoItem
          label="Skill evaluation scores"
          value={
            company.isSkillsEvaluationScoreEnabled ? 'Enabled' : 'Disabled'
          }
        />
        <InfoItem
          label="Display example behaviours on skills evaluation"
          value={company.isViewGuidanceEnabled ? 'Enabled' : 'Disabled'}
        />
      </div>
    </div>
  );
}

type InterviewEvaluationFormProps = {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
};

type InterviewEvaluationFormValues = {
  skillsEvaluationScore: string;
  viewGuidance: string;
};

function InterviewEvaluationForm({
  company,
  onCancelClick,
  onSave,
}: InterviewEvaluationFormProps) {
  const { handleMsgType } = useNotification();

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();

  const handleSave = (values: InterviewEvaluationFormValues) => {
    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        isSkillsEvaluationScoreEnabled:
          values.skillsEvaluationScore === EnableDisableValue.Enabled,
        isViewGuidanceEnabled:
          values.viewGuidance === EnableDisableValue.Enabled,
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Interview evaluation settings updated.',
          dismissable: true,
        });
        onSave();
      },
      onError: () => {
        handleMsgType({
          type: TNotification.error,
          title: 'Interview evaluation settings could not be updated',
          message: 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  const initialValues = {
    skillsEvaluationScore: company.isSkillsEvaluationScoreEnabled
      ? EnableDisableValue.Enabled
      : EnableDisableValue.Disabled,
    viewGuidance: company.isViewGuidanceEnabled
      ? EnableDisableValue.Enabled
      : EnableDisableValue.Disabled,
  };

  return (
    <Formik<InterviewEvaluationFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
    >
      {({ submitForm }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Interview Evaluation"
          formId="interview-evaluation-form"
          hasResetAllButton={false}
          data-testid={dataTestId}
        >
          <form
            onSubmit={e => e.preventDefault()}
            id="interview-evaluation-form-element"
            className={styles.form}
          >
            <FormikRadioButtonGroup
              id="skillsEvaluationScore"
              name="skillsEvaluationScore"
              options={enableDisableRadioButtonOptions}
              layout="horizontal"
              label="Skills evaluation scores"
              data-testid={`${dataTestId}__skills-evaluation-score-radio-group`}
              useFormikField={useField}
              className={styles.radioGroup}
            />
            <FormikRadioButtonGroup
              id="viewGuidance"
              name="viewGuidance"
              options={enableDisableRadioButtonOptions}
              layout="horizontal"
              label="Display example behaviours on skills evaluation"
              data-testid={`${dataTestId}__view-guidance-radio-group`}
              useFormikField={useField}
              className={styles.radioGroup}
            />
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
}
